import { Text, Tooltip } from "@chakra-ui/react";
import { MAX_POSSIBLE_DIVIDER_INDEX } from "@src/components/ui-kit/budget-path-select/constants";
import {
  getSelectedTagIndex,
  getTagCode,
  getTagColorSchemeByIndex,
  getTagSuffix,
} from "@src/components/ui-kit/budget-path-select/utils";
import type { IOption } from "@src/components/ui-kit/Select/types";
import {
  chakraComponents,
  type MultiValueGenericProps,
  type MultiValueRemoveProps,
  type ValueContainerProps,
} from "chakra-react-select";
import {
  Children,
  Fragment,
  isValidElement,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Icon } from "../Icon";

export const BudgetPathTagContainer = (props: MultiValueGenericProps) => {
  const selectedOptions = Array.isArray(props.selectProps.value)
    ? (props.selectProps.value as IOption[])
    : [];
  const data = props.data as IOption;
  const selectedIndex = getSelectedTagIndex(data.value, selectedOptions);
  const colorScheme = getTagColorSchemeByIndex(selectedIndex);

  return (
    <chakraComponents.MultiValueContainer
      {...props}
      sx={{
        ...props.sx,
        bg: colorScheme.backgroundColor,
        color: colorScheme.textColor,
        borderWidth: "1px",
        borderColor: colorScheme.borderColor,
      }}
    />
  );
};

export const BudgetPathTagRemove = (props: MultiValueRemoveProps) => {
  const selectedOptions = Array.isArray(props.selectProps.value)
    ? (props.selectProps.value as IOption[])
    : [];
  const data = props.data as IOption;
  const selectedIndex = getSelectedTagIndex(data.value, selectedOptions);
  const colorScheme = getTagColorSchemeByIndex(selectedIndex);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    props.innerProps.onClick?.(event);
    props.selectProps.onMenuOpen?.();
  };

  return (
    <chakraComponents.MultiValueRemove
      {...props}
      innerProps={{
        ...props.innerProps,
        onClick: handleClick,
      }}
      sx={{
        ...props.sx,
        color: colorScheme.textColor,
        ":hover": {
          bg: colorScheme.removeHoverBackgroundColor,
          color: colorScheme.textColor,
        },
      }}
    />
  );
};

export const BudgetPathTagLabel = (props: MultiValueGenericProps) => {
  const tagCode = getTagCode(props.data);
  const tagSuffix = getTagSuffix(props.data);
  const suffixRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!tagSuffix) {
      setIsTruncated(false);
      return;
    }

    const element = suffixRef.current;

    if (!element) return;

    const updateTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    updateTruncation();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateTruncation);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [suffixRef.current, tagSuffix, props.data.label]);

  return (
    <chakraComponents.MultiValueLabel {...props}>
      <Tooltip
        hasArrow
        isDisabled={!tagSuffix || !isTruncated}
        label={props.data.label}
        placement="top"
      >
        <Text
          ref={suffixRef}
          overflow="hidden"
          minW="50px"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {tagCode}
          {!!tagSuffix && (
            <>
              &nbsp;
              {tagSuffix}
            </>
          )}
        </Text>
      </Tooltip>
    </chakraComponents.MultiValueLabel>
  );
};

export const BudgetPathValueContainer = (props: ValueContainerProps) => {
  const children = Children.toArray(props.children);
  const selectedValues = props.getValue();

  if (!selectedValues.length) {
    return <chakraComponents.ValueContainer {...props} />;
  }

  const valueChildren = children.slice(0, selectedValues.length);
  const remainingChildren = children.slice(selectedValues.length);

  return (
    <chakraComponents.ValueContainer {...props}>
      {valueChildren.map((child, index) => (
        <Fragment
          key={isValidElement(child) && child.key !== null ? child.key : index}
        >
          {child}
          {index <= MAX_POSSIBLE_DIVIDER_INDEX && (
            <Icon name="chevron-right" color="gray.500" />
          )}
        </Fragment>
      ))}
      {remainingChildren}
    </chakraComponents.ValueContainer>
  );
};
